import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { Spinner, usePageNavigation } from '@shared/components';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriers } from '../../hooks/useCouriers';
import { useMapShipmentStatus } from '../../hooks/useMapShipmentStatus';
import { useShipmentStatusFilters } from '../../hooks/useShipmentStatusFilters';
import { useShipmentStatusMappingStats } from '../../hooks/useShipmentStatusMappingStats';
import { useShipmentStatusMappings } from '../../hooks/useShipmentStatusMappings';
import { useShipmentStatuses } from '../../hooks/useShipmentStatuses';
import type { ShipmentStatusMapping } from '../../models/shipmentStatusMapping.types';
import { ShipmentStatusMappingView } from '../display/ShipmentStatusMappingView';
import { ErrorState } from '../feedback/ErrorState';
import { InteractiveMappingModal, InteractiveMappingModalRef } from '../modals/InteractiveMappingModal';
import { MapShipmentStatusModal, MapShipmentStatusModalRef } from '../modals/MapShipmentStatusModal';

export const ShipmentStatusMappingContainer = () => {
  const { t } = useTranslation('shipmentStatusMappingModule');
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const mapModalRef = useRef<MapShipmentStatusModalRef>(null);
  const interactiveMappingModalRef = useRef<InteractiveMappingModalRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const canUpdate = hasPermission('shipment_status_mappings.update');

  const { search, courierId, statusId, apiFilters, updateFilters, hasActiveFilters } = useShipmentStatusFilters();

  const { couriers, isLoading: isLoadingCouriers } = useCouriers();
  const { shipmentStatuses, isLoading: isLoadingStatuses } = useShipmentStatuses();
  const { total: allMappingsCount, isLoading: isLoadingStats } = useShipmentStatusMappingStats();

  const {
    mappings: rawMappings,
    isLoading: isLoadingMappings,
    isFetchingFilters,
    isFetchingNextPage,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    total: rawTotal,
  } = useShipmentStatusMappings(apiFilters);
  const { mutateAsync: mapStatus } = useMapShipmentStatus();

  // El backend soporta "sin mapear" vía API; "mapeados" se filtra en memoria sobre lo cargado.
  const { mappings, filteredTotal } = useMemo(() => {
    if (statusId === 'mapped') {
      const filtered = rawMappings.filter((mapping) => mapping.shipment_status !== null);
      return { mappings: filtered, filteredTotal: filtered.length };
    }

    return { mappings: rawMappings, filteredTotal: rawTotal };
  }, [rawMappings, rawTotal, statusId]);

  useEffect(() => {
    if (!isLoadingMappings && !isLoadingStatuses && !isLoadingCouriers && !isLoadingStats && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingMappings, isLoadingStatuses, isLoadingCouriers, isLoadingStats, isInitialLoad]);

  const breadcrumbs = useMemo(() => [{ label: t('shipmentStatusMapping.pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  const handleOpenInteractiveMapping = useCallback(() => {
    interactiveMappingModalRef.current?.open();
  }, []);

  const handleEditMapping = useCallback((mapping: ShipmentStatusMapping) => {
    mapModalRef.current?.open(mapping);
  }, []);

  const handleUnmapMapping = useCallback(
    (mapping: ShipmentStatusMapping) => {
      confirmModalRef.current?.open(
        async () => {
          try {
            await mapStatus({
              id: mapping.id,
              request: { shipment_status_id: null },
            });

            toast({
              title: t('shipmentStatusMapping.messages.unmapSuccess'),
            });

            refetch();
          } catch (err: any) {
            toast({
              title: t('shipmentStatusMapping.messages.unmapError'),
              description: err?.response?.data?.error?.message || err?.response?.data?.message || err?.message,
              variant: 'destructive',
            });
          }
        },
        undefined,
        t('shipmentStatusMapping.modal.unmap'),
        t('shipmentStatusMapping.modal.unmapConfirm'),
        t('shipmentStatusMapping.modal.unmap'),
        t('shipmentStatusMapping.modal.cancel'),
      );
    },
    [mapStatus, refetch, t, toast],
  );

  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isInitialLoad) {
    return (
      <div className="flex justify-center py-16" data-testid="shipment-status-mapping-loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-16">
        <ErrorState error={error as Error} onRetry={refetch} className="h-auto" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <MapShipmentStatusModal ref={mapModalRef} shipmentStatuses={shipmentStatuses} onSuccess={handleModalSuccess} />

      <InteractiveMappingModal
        ref={interactiveMappingModalRef}
        shipmentStatuses={shipmentStatuses}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal ref={confirmModalRef} />

      <ShipmentStatusMappingView
        mappings={mappings}
        filteredTotal={filteredTotal}
        allMappingsCount={allMappingsCount}
        couriers={couriers}
        search={search}
        courierId={courierId}
        statusId={statusId}
        hasActiveFilters={hasActiveFilters}
        canUpdate={canUpdate}
        isFetchingFilters={isFetchingFilters}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onFiltersChange={updateFilters}
        onEditMapping={handleEditMapping}
        onUnmapMapping={handleUnmapMapping}
        onMapStatuses={handleOpenInteractiveMapping}
      />
    </div>
  );
};
